// TrafficSection.tsx — onglet « Trafic » du Dashboard : les connexions aux comptes
//
// Refait le 2026-10-17 d'après la maquette validée par l'utilisateur
// (https://claude.ai/artifact/MtJ8i9W8i9G2uHH3CcnL7N), à la place de la seule table
// de LastLoginV2 : chiffres du haut, alerte quand une même adresse IP accumule les
// échecs, connexions par heure (par jour au-delà d'une journée), villes, puis le
// journal. Tout se calcule dans le navigateur à partir de la liste que rend
// admin-api AuthenticationResultList, relue chaque minute.
//
// Repris le même jour : l'onglet prend toute la largeur (il n'est plus dans le cadre
// de 520 px de l'ancienne table), « Aujourd'hui » se suit par minute (la dernière
// heure), par 10 minutes ou par heure, et une carte place les connexions réussies
// (vert) et les échecs (rouge) d'après la position de leur adresse IP.
//
// Survol d'une barre du graphe : la carte, les villes et le journal ne montrent plus
// que les connexions de cette tranche. Un clic fige la tranche (la souris peut alors
// partir), un second clic sur la même barre revient à toute la période.
//
// Les connexions passées par un VPN, un hébergeur ou un relais (fournisseur reconnu
// dans hostingProviders.ts) portent une étiquette dans le journal, sont comptées en
// haut et peuvent être isolées.
//
// Une journée seule (« Aujourd'hui » ou « Personnaliser » sur un jour) se suit par
// minute, par 10 minutes ou par heure. Par minute, le graphe montre une fenêtre de
// 60 minutes qu'un ascenseur déplace minute par minute dans la journée ; aujourd'hui,
// la fenêtre suit l'heure qu'il est tant qu'on ne la déplace pas.
//
// Pas de bouton « Bloquer l'adresse » : il n'existe pas encore de liste de blocage
// (ni dans admin-api, ni dans HAProxy). « Voir les tentatives » filtre le journal.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import styles from './ChannelsSection.module.scss';
import tabs   from './DashboardTabs.module.scss';

import OpenStreetMapV2   from '../../widget/dataDisplay/maps/OpenStreetMapV2';
import ExtendablePanel   from '../../widget/windows/ExtendablePanel';
import Box               from '../../widget/windows/Box';
import UserInformations2 from '../userManagment/UserInformations2';

import { UserAuthenticationResult } from '../../../dataTypes/userManagment/types';
import { Coordinate } from '../../../dataTypes/geolocationManagment/types';
import { getUserAuthenticationResultList } from '../../../backendServices/userManagment/api';
import { getAdminEventContext, getAnonymousVisits, AnonymousVisit } from '../../../backendServices/adminManagment/api_adminEvents';
import { Segment, plural, hour, dayLabel, DateRange, customRange, todayStart as customFrom, todayEnd as customTo } from './dashboardShared';

import { hostingOf, HOSTING_LABEL, HostingKind } from './hostingProviders';

/** Une ligne du journal : une tentative de connexion à un compte, ou (vue « Spectateur ») un visionnage sans compte. */
type Log = UserAuthenticationResult & { anon?: AnonymousVisit };

const ANON_ORIGIN: Record<string, string> = { PORTAL_WEB: 'kleon.tv', PORTAL_TVOS: 'appletv', PORTAL_FIRETV: 'firetv' };
const span = (s: number) => s < 60 ? `${s} s` : s < 3600 ? `${Math.floor(s / 60)} min ${String(s % 60).padStart(2, '0')} s` : `${Math.floor(s / 3600)} h ${String(Math.floor(s % 3600 / 60)).padStart(2, '0')} min`;

/** Un visionnage anonyme rendu comme une connexion réussie, pour réutiliser tout l'écran (chiffres, graphique, carte, villes, journal). */
const fromVisit = (v: AnonymousVisit): Log => ({
  date: new Date(v.at), status: 'Succès', id: -v.id, firstName: '', lastName: '', email: v.target.name, ipAddress: v.ip,
  country: v.location?.country ?? '', city: v.location?.city ?? '', lat: v.location?.lat ?? 0, lon: v.location?.lon ?? 0, isp: v.location?.isp ?? '',
  origin: v.origin ? (ANON_ORIGIN[v.origin] ?? v.origin) : null, role: null, app: null, firstTime: false, anon: v,
});

type Period = 'today' | 'yesterday' | 'week' | 'month' | 'custom';
type Origin = 'portal' | 'room' | 'ios' | 'android' | 'appletv' | 'firetv' | 'other';
type Step   = 'minute' | 'tenMinutes' | 'hour';
type MapShow = 'all' | 'ok' | 'ko';

const REFRESH_MS   = 60_000;
const REFRESH_FAST = 20_000;             // suivi minute par minute : relevé plus serré
const COLOR_OK     = '#22C55E';
const COLOR_KO     = '#EF4444';
const FRESH_MS     = 60_000;
const BURST_COUNT  = 3;                  // échecs depuis une même IP…
const BURST_MS     = 10 * 60_000;        // …en dix minutes : on le signale
const LOG_PAGE     = 100;
const ORIGIN_LABEL: Record<Origin, string> = { portal: 'kleon.tv', room: 'Room', ios: 'Kleon Studio iPhone', android: 'Kleon Studio Android', appletv: 'Apple TV', firetv: 'Fire TV', other: 'Autre' };
const ORIGIN_CLASS: Record<Origin, string> = { portal: tabs.oPortal, room: tabs.oRoom, ios: tabs.oIos, android: tabs.oAndroid, appletv: tabs.oAppleTv, firetv: tabs.oFireTv, other: tabs.oOther };
const ORIGIN_BAR  : Record<Origin, string> = { portal: tabs.cPortal, room: tabs.cRoom, ios: tabs.cIos, android: tabs.cAndroid, appletv: tabs.cAppleTv, firetv: tabs.cFireTv, other: tabs.cHidden };

const isOk = (l: Log) => l.status === 'Succès';
const originOf = (l: Log): Origin => {
  if (l.app === 'ios') return 'ios';
  if (l.app === 'android') return 'android';
  // Spectateurs (customer-api, 2026-10-17) : l'app Apple TV et l'app Fire TV sont notées par leur nom.
  if (l.origin === 'appletv') return 'appletv';
  if (l.origin === 'firetv') return 'firetv';
  const host = l.origin ?? '';
  if (host === 'kleon.tv' || host.endsWith('.kleon.tv')) return 'portal';
  if (host.startsWith('room.')) return 'room';
  return 'other';
};
const placeOf = (l: Log) => [l.city, l.country].filter(Boolean).join(', ');

/** Ligne d'un point choisi sur la carte (même bleu que les sélections des autres onglets). */
const PLACE_ROW: React.CSSProperties = { background: 'rgba(44,125,225,0.22)', boxShadow: 'inset 4px 0 0 #2C7DE1' };

/** Clé du point de carte d'une ligne (lieu arrondi + résultat), null si sans position. */
const placeKeyOf = (l: Log): string | null => {
  const lat = Number(l.lat), lng = Number(l.lon);
  if (!isFinite(lat) || !isFinite(lng) || (lat === 0 && lng === 0)) return null;
  return `${lat.toFixed(2)}|${lng.toFixed(2)}|${isOk(l) ? 1 : 0}`;
};

const range = (p: Period, from: Date, to: Date): [Date, Date] => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
  const end   = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
  if (p === 'yesterday') {
    // La veille en entier, de 00:00:00 à 23:59:59 : un jour unique, jamais « en direct ».
    const y = now.getDate() - 1;
    return [ new Date(now.getFullYear(), now.getMonth(), y, 0, 0, 0), new Date(now.getFullYear(), now.getMonth(), y, 23, 59, 59) ];
  }
  if (p === 'week')  return [ new Date(start.getTime() - 6 * 86_400_000), end ];
  if (p === 'month') return [ new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0), end ];
  if (p === 'custom') return customRange(from, to);
  return [ start, end ];
};

const midnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
/** Jours de calendrier touchés par la plage (« Personnaliser » à la minute : 22:00 → 02:00 en touche deux). */
const calendarDays = (start: Date, end: Date) => Math.round((midnight(end) - midnight(start)) / 86_400_000) + 1;

/** Un maximum d'axe « rond » (10, 20, 50, 100…) et son pas, pour 4 à 5 graduations. */
const niceScale = (max: number): [number, number] => {
  const raw  = Math.max(4, max) / 4;
  const pow  = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = [1, 2, 5, 10].map(m => m * pow).find(s => s >= raw) ?? 10 * pow;
  return [ Math.ceil(Math.max(1, max) / step) * step, step ];
};

// ── Connexions par heure / par jour ──────────────────────────────────────────

/** Une barre du graphe : sa tranche [from, to[ en millisecondes, et ses connexions. */
type Bucket = { label: string; ok: number; ko: number; current: boolean; from: number; to: number };

/** Une tuile, cliquable ou non : la vue des visionnages n'a rien à filtrer sur celle-ci. */
const KpiTile: React.FC<{ selectable: boolean; on: boolean; title: string; onClick: () => void; children: React.ReactNode }> = ({ selectable, on, title, onClick, children }) =>
  selectable
    ? <button type="button" className={`${styles.kpi} ${styles.kpiBtn} ${on ? styles.kpiOn : ''}`} aria-pressed={on} title={title} onClick={onClick}>{children}</button>
    : <div className={styles.kpi}>{children}</div>;

const Chart: React.FC<{ buckets: Bucket[]; labelEvery: number; activeFrom: number | null; pinnedFrom: number | null;
                        onHover: (i: number | null) => void; onPick: (i: number) => void }> =
  ({ buckets, labelEvery, activeFrom, pinnedFrom, onHover, onPick }) => {
  const W = 960, H = 240, L = 34, R = 8, T = 16, B = 26;
  const [ max, step ] = niceScale(Math.max(...buckets.map(b => b.ok + b.ko), 0));
  const cw = (W - L - R) / Math.max(1, buckets.length);
  const y  = (v: number) => T + (H - T - B) * (1 - v / max);
  const gap = Math.min(6, cw * 0.25);
  const ticks: number[] = [];
  for (let v = 0; v <= max; v += step) ticks.push(v);

  return (
    <svg className={tabs.chart} viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Connexions réussies et échecs"
         onMouseLeave={() => onHover(null)}>
      {ticks.map(v => (
        <g key={v}>
          <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke="#1C3557" strokeWidth="1" />
          <text x={L - 6} y={y(v) + 4} textAnchor="end">{v}</text>
        </g>
      ))}
      {buckets.map((b, i) => {
        const x = L + i * cw + gap / 2, w = Math.max(1, cw - gap);
        const active = activeFrom === b.from, pinned = pinnedFrom === b.from;
        const dim = activeFrom !== null && !active;
        return (
          <g key={b.from} opacity={dim ? 0.35 : 1}>
            {(active || pinned) && <rect x={L + i * cw} y={T} width={cw} height={H - T - B} fill={pinned ? 'rgba(245,158,11,0.10)' : 'rgba(93,151,241,0.10)'} />}
            {b.ok + b.ko === 0
              ? <rect x={x} y={y(0) - 2} width={w} height={2} fill="#1C3557" />
              : <>
                  <rect x={x} y={y(b.ok)} width={w} height={y(0) - y(b.ok)} rx={Math.min(2, w / 2)} fill={active || b.current ? '#5D97F1' : '#2C7DE1'} />
                  {b.ko > 0 && <rect x={x} y={y(b.ok + b.ko)} width={w} height={y(b.ok) - y(b.ok + b.ko)} rx={Math.min(2, w / 2)} fill="#DC2626" />}
                </>}
            {pinned && <rect x={L + i * cw + 0.5} y={T + 0.5} width={Math.max(1, cw - 1)} height={H - T - B - 1} fill="none" stroke="#F59E0B" strokeWidth="1.5" />}
            {(active || (b.current && activeFrom === null)) && b.ok + b.ko > 0 &&
              <text className={tabs.chartValue} x={x + w / 2} y={y(b.ok + b.ko) - 5} textAnchor="middle">{b.ok + b.ko}</text>}
            {i % labelEvery === 0 && <text x={x + w / 2} y={H - 8} textAnchor="middle">{b.label}</text>}
            {/* Zone de survol et de clic : toute la hauteur de la tranche, plus facile à viser qu'une barre fine. */}
            {/* Le cadre blanc du navigateur restait après un clic (la zone gardait le focus) :
                un clic de souris rend le focus, et seul le clavier montre un repère (classe barHit). */}
            <rect x={L + i * cw} y={T} width={cw} height={H - T - B} fill="transparent" className={tabs.barHit}
                  role="button" tabIndex={0}
                  aria-label={`${b.label} : ${b.ok} réussie${b.ok > 1 ? 's' : ''}, ${b.ko} échec${b.ko > 1 ? 's' : ''}${pinned ? ' (figée)' : ''}`}
                  onMouseEnter={() => onHover(i)} onFocus={() => onHover(i)} onBlur={() => onHover(null)}
                  onClick={e => { onPick(i); if (e.detail > 0) (e.currentTarget as SVGRectElement).blur(); }}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPick(i); } }}>
              <title>{`${b.label} : ${b.ok} réussie${b.ok > 1 ? 's' : ''}, ${b.ko} échec${b.ko > 1 ? 's' : ''}`}</title>
            </rect>
          </g>
        );
      })}
    </svg>
  );
};

// ── Fournisseur, avec l'étiquette VPN / hébergeur / relais ──────────────────

const KIND_CLASS: Record<HostingKind, string> = { vpn: tabs.hVpn, cloud: tabs.hCloud, relay: tabs.hRelay };

const Provider: React.FC<{ isp: string | null | undefined }> = ({ isp }) => {
  const h = hostingOf(isp);
  return (
    <span className={tabs.provider}>
      <span>{isp || '—'}</span>
      {h && <span className={`${tabs.hostTag} ${KIND_CLASS[h.kind]}`} title={`${h.label} : connexion par un VPN, un hébergeur ou un relais ; la ville affichée est celle du serveur`}>{HOSTING_LABEL[h.kind]}</span>}
    </span>
  );
};

// ── Composant ────────────────────────────────────────────────────────────────

// `creatorMode` (2026-10-27) : onglet Trafic d'un créateur non superadmin — uniquement les visiteurs
// anonymes de ses chaînes et vidéos, sans adresse IP, fournisseur ni système / navigateur (l'API ne les envoie pas).
const TrafficSection: React.FC<{ adminId?: number; creatorMode?: boolean }> = ({ adminId, creatorMode = false }) => {
  const [ period , setPeriod  ] = useState<Period>('today');
  const [ from   , setFrom    ] = useState<Date>(customFrom);
  const [ to     , setTo      ] = useState<Date>(customTo);
  const [ rawLogs, setRawLogs  ] = useState<Log[]>([]);   // tout ce que le serveur a rendu ; `logs` en est la part qui répond à la recherche
  const [ loading, setLoading ] = useState(false);
  const [ error  , setError   ] = useState(false);
  const [ readAt , setReadAt  ] = useState<Date | null>(null);
  const [ ip     , setIp      ] = useState<string | null>(null);
  const journalRef = useRef<HTMLDivElement | null>(null);
  // Vue « Spectateur » : réservée aux super-admins, le serveur dit si le compte l'est.
  const [ view    , setView    ] = useState<'accounts' | 'anonymous'>('accounts');
  const [ query   , setQuery   ] = useState('');
  const [ isSuper , setIsSuper ] = useState(false);
  useEffect(() => {
    if (!adminId) { setIsSuper(false); return; }
    let cancelled = false;
    getAdminEventContext(adminId).then(r => { if (!cancelled) setIsSuper(r.ok && r.data.superadmin); });
    return () => { cancelled = true; };
  }, [adminId]);
  const [ more   , setMore    ] = useState(1);
  const [ user   , setUser    ] = useState(0);
  const [ step   , setStep    ] = useState<Step>('hour');
  // Fin de la fenêtre « par minute », en minutes depuis minuit (fin exclue) ; null =
  // position par défaut : l'heure qu'il est aujourd'hui, l'heure la plus chargée d'un jour passé.
  const [ minuteEnd, setMinuteEnd ] = useState<number | null>(null);
  const [ mapShow, setMapShow ] = useState<MapShow>('all');
  const [ onlyHosting, setOnlyHosting ] = useState(false);
  // Tuile choisie : graphique, carte, villes et journal se limitent à ces lignes ; les chiffres
  // des tuiles restent ceux de toute la période, pour pouvoir passer de l'une à l'autre.
  const [ tile, setTile ] = useState<'fails' | 'firsts' | Origin | null>(null);
  // Tranche survolée (index dans les barres affichées) et tranche figée par un clic
  // (gardée par ses bornes : elle survit aux relevés qui décalent les barres).
  const [ hovered, setHovered ] = useState<number | null>(null);
  const [ pinned , setPinned  ] = useState<{ from: number; to: number; label: string } | null>(null);

  const anonymous = creatorMode || (view === 'anonymous' && isSuper);
  const loadSeq   = useRef(0);   // une réponse qui arrive après un changement de vue ne doit pas écraser l'autre vue

  const load = useCallback(async () => {
    const [ start, end ] = range(period, from, to);
    const seq = ++loadSeq.current;
    setLoading(true);
    try {
      if (anonymous) {
        if (!adminId) { setError(true); return; }
        const res = await getAnonymousVisits(adminId, start, end);
        if (seq !== loadSeq.current) return;
        if (res.ok) { setRawLogs(res.data.visits.map(fromVisit)); setError(false); setReadAt(new Date()); } else setError(true);
        return;
      }
      const res = await getUserAuthenticationResultList(start, end, undefined, true);
      if (seq !== loadSeq.current) return;
      if (res.code === 1) {
        setRawLogs([...res.payload].sort((a, b) => (b.date?.getTime() ?? 0) - (a.date?.getTime() ?? 0)));
        setError(false);
        setReadAt(new Date());
      } else setError(true);
    } catch { setError(true); }
    finally { if (seq === loadSeq.current) setLoading(false); }
  }, [period, from, to, anonymous, adminId]);

  // Changer de vue : les lignes de l'autre vue ne veulent rien dire ici, et les filtres non plus.
  useEffect(() => { setRawLogs([]); setPinned(null); setHovered(null); setIp(null); setQuery(''); setOnlyHosting(false); setPlacePick(null); }, [anonymous]);

  // La journée affichée : un seul jour (aujourd'hui, ou « Personnaliser » du jour au même jour).
  const [ rangeStart, rangeEnd ] = range(period, from, to);
  const singleDay = period === 'today' || calendarDays(rangeStart, rangeEnd) === 1;
  const dayStart  = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), rangeStart.getDate()).getTime();
  const todayStart = (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.getTime(); })();
  const live      = singleDay && dayStart === todayStart;
  const fast = live && step === 'minute' && minuteEnd === null;
  useEffect(() => {
    load();
    const t = window.setInterval(load, fast ? REFRESH_FAST : REFRESH_MS);
    return () => window.clearInterval(t);
  }, [load, fast]);

  useEffect(() => { setMore(1); }, [period, from, to, ip, pinned, onlyHosting, tile]);
  useEffect(() => { setTile(null); }, [view]);
  // Changer de période ou de pas redessine d'autres barres : la tranche figée n'a plus de sens.
  useEffect(() => { setPinned(null); setHovered(null); }, [period, from, to, step]);
  useEffect(() => { setMinuteEnd(null); }, [period, from, to]);

  // ── Recherche : porte sur tout l'écran (chiffres, graphique, carte, villes, journal) ──
  // On cherche dans ce que les lignes affichent : compte, type, résultat ou état, heure et jour, origine, lieu,
  // adresse IP, fournisseur (et VPN / hébergeur), et pour les visionnages contenu, créateur, appareil, système, navigateur.
  const needle = query.trim().toLowerCase();
  const haystack = (l: Log): string => {
    const a = l.anon;
    return [
      l.email, l.firstName, l.lastName, l.role === 'créateur' ? 'créateur' : l.role === 'user' ? 'spectateur' : '',
      a ? (a.live ? 'en cours' : `terminé ${span(a.seconds)} ${a.endReason ?? ''}`) : (isOk(l) ? 'réussie succès' : `refusée échec ${l.reason ?? ''} ${l.unknown ? 'compte inconnu' : ''}`),
      l.firstTime ? '1re première fois' : '',
      l.date ? `${l.date.toLocaleString('fr-FR')} ${dayLabel(l.date.toISOString())}` : '',
      ORIGIN_LABEL[originOf(l)], l.origin, a?.origin,
      a?.account, l.city, l.country, l.ipAddress, l.isp, hostingOf(l.isp) ? `vpn hébergeur ${HOSTING_LABEL[hostingOf(l.isp)!.kind]}` : '',
      a ? `${a.target.type === 'video' ? 'vidéo' : 'chaîne'} ${a.target.name} ${a.creatorName ?? ''} ${a.client?.device ?? ''} ${a.client?.os ?? ''} ${a.client?.browser ?? ''} ${a.client?.app ?? ''} ${a.userAgent ?? ''}` : '',
    ].join(' ').toLowerCase();
  };
  const logs = useMemo(() => needle ? rawLogs.filter(l => haystack(l).includes(needle)) : rawLogs,
                       [rawLogs, needle, anonymous]);   // eslint-disable-line react-hooks/exhaustive-deps

  // ── Chiffres ──
  const ok       = logs.filter(isOk);
  const fails    = logs.filter(l => !isOk(l));
  const accounts = new Set(ok.map(l => l.id)).size;
  const firsts   = new Set(ok.filter(l => l.firstTime).map(l => l.id)).size;
  const lastHour = logs.filter(l => l.date && Date.now() - l.date.getTime() < 3_600_000).length;
  const viaHosting = logs.filter(l => hostingOf(l.isp) !== null);
  const failsViaHosting = viaHosting.filter(l => !isOk(l)).length;
  const failRate = logs.length ? fails.length / logs.length * 100 : 0;

  const origins = useMemo(() => {
    const c: Record<Origin, number> = { portal: 0, room: 0, ios: 0, android: 0, appletv: 0, firetv: 0, other: 0 };
    ok.forEach(l => { c[originOf(l)]++; });
    return c;
  }, [ok]);
  const originTotal = Math.max(1, ok.length);
  const topOrigin = (Object.keys(origins) as Origin[]).sort((a, b) => origins[b] - origins[a])[0];

  const tiledLogs = useMemo(() => tile === null ? logs
    : logs.filter(l => tile === 'fails' ? !isOk(l)
                     : tile === 'firsts' ? isOk(l) && l.firstTime
                     : isOk(l) && originOf(l) === tile), [logs, tile]);

  // ── Échecs groupés par adresse IP ──
  const failsByIp = useMemo(() => {
    const m = new Map<string, Log[]>();
    fails.forEach(l => { if (l.ipAddress) m.set(l.ipAddress, [...(m.get(l.ipAddress) ?? []), l]); });
    return m;
  }, [fails]);
  const worstIpCount = Math.max(0, ...Array.from(failsByIp.values()).map(v => v.length));

  // La pire rafale : le plus d'échecs d'une même IP dans une fenêtre de dix minutes.
  const burst = useMemo(() => {
    let best: { ip: string; count: number; minutes: number; items: Log[] } | null = null;
    failsByIp.forEach((items, addr) => {
      const sorted = [...items].filter(l => l.date).sort((a, b) => a.date!.getTime() - b.date!.getTime());
      let i = 0;
      for (let j = 0; j < sorted.length; j++) {
        while (sorted[j].date!.getTime() - sorted[i].date!.getTime() > BURST_MS) i++;
        const count = j - i + 1;
        if (count >= BURST_COUNT && (!best || count > best.count)) {
          best = { ip: addr, count, minutes: Math.max(1, Math.round((sorted[j].date!.getTime() - sorted[i].date!.getTime()) / 60_000)), items: sorted.slice(i, j + 1) };
        }
      }
    });
    return best as { ip: string; count: number; minutes: number; items: Log[] } | null;
  }, [failsByIp]);

  // ── Graphique ──
  const [ start, end ] = range(period, from, to);
  const byHour = period === 'today' || calendarDays(start, end) === 1;
  // ── Fenêtre « par minute » ──
  const nowMinute = Math.floor((Date.now() - dayStart) / 60_000) + 1;          // fin exclue
  const maxEnd    = live ? Math.max(60, Math.min(1440, nowMinute)) : 1440;
  const busiestEnd = useMemo(() => {
    const perHour = new Array(24).fill(0);
    tiledLogs.forEach(l => { if (l.date && l.date.getTime() >= dayStart && l.date.getTime() < dayStart + 86_400_000) perHour[l.date.getHours()]++; });
    const h = perHour.indexOf(Math.max(...perHour));
    return Math.max(60, Math.min(1440, (h + 1) * 60));
  }, [tiledLogs, dayStart]);
  const windowEnd = Math.max(60, Math.min(maxEnd, minuteEnd ?? (live ? maxEnd : busiestEnd)));
  const clock = (m: number) => new Date(dayStart + m * 60_000).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  const moveWindow = (m: number) => {
    const v = Math.max(60, Math.min(maxEnd, m));
    setMinuteEnd(live && v >= maxEnd ? null : v);   // revenu au bout, aujourd'hui : on suit de nouveau l'heure
  };

  const buckets = useMemo((): Bucket[] => {
    const count = (out: Bucket[]) => {
      tiledLogs.forEach(l => {
        if (!l.date) return;
        const t = l.date.getTime();
        const b = out.find(x => t >= x.from && t < x.to);
        if (b) { if (isOk(l)) b.ok++; else b.ko++; }
      });
      return out;
    };
    if (singleDay && step !== 'hour') {
      const size = step === 'minute' ? 60_000 : 600_000;
      let first: number, n: number;
      if (step === 'minute') {
        // 60 minutes, déplacées par l'ascenseur.
        first = dayStart + (windowEnd - 60) * 60_000;
        n = 60;
      } else {
        // Par 10 minutes : de minuit à maintenant aujourd'hui, toute la journée sinon.
        first = dayStart;
        n = live ? Math.floor((Date.now() - dayStart) / size) + 1 : 144;
      }
      return count(Array.from({ length: n }, (_, i) => {
        const t = new Date(first + i * size);
        return { label: t.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }), ok: 0, ko: 0,
                 current: live && i === n - 1 && (step !== 'minute' || windowEnd >= maxEnd),
                 from: first + i * size, to: first + (i + 1) * size };
      }));
    }
    if (byHour) {
      const nowH = new Date().getHours();
      const d0 = new Date(start.getFullYear(), start.getMonth(), start.getDate());
      return count(Array.from({ length: 24 }, (_, h) => ({
        label: `${String(h).padStart(2, '0')} h`, ok: 0, ko: 0, current: period === 'today' && h === nowH,
        from: new Date(d0.getFullYear(), d0.getMonth(), d0.getDate(), h).getTime(),
        to  : new Date(d0.getFullYear(), d0.getMonth(), d0.getDate(), h + 1).getTime(),
      })));
    }
    const days = Math.min(62, calendarDays(start, end));
    return count(Array.from({ length: days }, (_, i) => {
      const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
      return { label: d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'numeric' }), ok: 0, ko: 0,
               current: d.toDateString() === new Date().toDateString(),
               from: d.getTime(), to: new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).getTime() };
    }));
  }, [tiledLogs, byHour, singleDay, live, period, step, readAt, windowEnd, dayStart, start.getTime(), end.getTime()]);   // eslint-disable-line react-hooks/exhaustive-deps
  const chartTitle = !singleDay ? 'Connexions par jour'
                   : step === 'minute' ? `Connexions par minute, de ${clock(windowEnd - 60)} à ${clock(windowEnd)}`
                   : step === 'tenMinutes' ? 'Connexions par 10 minutes' : 'Connexions par heure';
  const labelEvery = singleDay
    ? (step === 'minute' ? 10 : step === 'tenMinutes' ? (buckets.length > 72 ? 12 : 6) : 6)
    : Math.max(1, Math.ceil(buckets.length / 8));

  // ── La tranche choisie : figée par un clic, sinon survolée ──
  const hoveredBucket = hovered !== null ? buckets[hovered] ?? null : null;
  const slice = pinned ?? (hoveredBucket ? { from: hoveredBucket.from, to: hoveredBucket.to, label: hoveredBucket.label } : null);
  const sliceLogs = useMemo(() => slice === null ? tiledLogs
    : tiledLogs.filter(l => l.date && l.date.getTime() >= slice.from && l.date.getTime() < slice.to),
    [tiledLogs, slice?.from, slice?.to]);   // eslint-disable-line react-hooks/exhaustive-deps
  const sliceText = slice === null ? '' : (() => {
    const a = new Date(slice.from), b = new Date(slice.to);
    const t = (d: Date) => d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    return slice.to - slice.from >= 86_400_000 ? `le ${a.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}` : `de ${t(a)} à ${t(b)}`;
  })();
  const pick = (i: number) => {
    const b = buckets[i];
    if (!b) return;
    setPinned(p => p && p.from === b.from ? null : { from: b.from, to: b.to, label: b.label });
  };

  // ── Carte : un point par lieu et par résultat (réussies en vert, échecs en rouge) ──
  const mapPoints = useMemo(() => {
    const m = new Map<string, { lat: number; lng: number; ok: boolean; n: number; place: string; emails: Set<string> }>();
    let unlocated = 0;
    for (const l of sliceLogs) {
      const good = isOk(l);
      if ((mapShow === 'ok' && !good) || (mapShow === 'ko' && good)) continue;
      const lat = Number(l.lat), lng = Number(l.lon);
      if (!isFinite(lat) || !isFinite(lng) || (lat === 0 && lng === 0)) { unlocated++; continue; }
      const k = `${lat.toFixed(2)}|${lng.toFixed(2)}|${good ? 1 : 0}`;
      const e = m.get(k) ?? { lat, lng, ok: good, n: 0, place: placeOf(l) || 'Lieu inconnu', emails: new Set<string>() };
      e.n++; e.emails.add(l.email);
      m.set(k, e);
    }
    // Clé de chaque point, dans l'ordre de `coordinates` : le clic sur la carte rend un index.
    const keys = Array.from(m.keys());
    const coordinates: Coordinate[] = Array.from(m.values()).map(e => ({
      lat  : e.lat,
      // Échecs et réussies au même endroit : l'échec est décalé d'un rien pour rester visible.
      lng  : e.ok || !m.has(`${e.lat.toFixed(2)}|${e.lng.toFixed(2)}|1`) ? e.lng : e.lng + 0.04,
      info1: e.place,
      info2: e.ok ? (anonymous ? plural(e.n, 'visionnage') : `${plural(e.n, 'connexion réussie', 'connexions réussies')}`)
                  : `${plural(e.n, 'échec', 'échecs')} · ${Array.from(e.emails).slice(0, 2).join(', ')}${e.emails.size > 2 ? '…' : ''}`,
      color: e.ok ? COLOR_OK : COLOR_KO,
    }));
    return { coordinates, keys, unlocated, places: new Set(Array.from(m.values()).map(e => `${e.lat.toFixed(2)}|${e.lng.toFixed(2)}`)).size };
  }, [sliceLogs, mapShow, anonymous]);

  // ── Clic sur un point de la carte (2026-10-24) : sélectionne ses lignes du journal ──
  // Un point = un lieu et un résultat (même clé que ci-dessus). Ses lignes sont surlignées,
  // la première amenée à l'écran ; les filtres adresse / VPN qui les masqueraient sont
  // levés et le journal s'allonge jusqu'à elle. Un clic hors point, ou sur le même point,
  // efface la sélection.
  const [ placePick, setPlacePick ] = useState<string | null>(null);
  const [ pickTick , setPickTick  ] = useState(0);
  const scrolledTick = useRef(0);
  const mapClick = (i: number | null) => {
    const k = i === null ? null : mapPoints.keys[i] ?? null;
    if (k === null || k === placePick) { setPlacePick(null); return; }
    if (!sliceLogs.some(l => placeKeyOf(l) === k)) return;
    setIp(null); setOnlyHosting(false);
    setPlacePick(k);
    setPickTick(t => t + 1);
  };
  // Après le rendu (donc après la remise à la 1re page que provoque la levée des filtres) :
  // allonger le journal jusqu'à la première ligne du lieu, puis la montrer, une fois par clic.
  useEffect(() => {
    if (!placePick || pickTick === 0 || scrolledTick.current === pickTick) return;
    const first = sliceLogs.filter(l => (!ip || l.ipAddress === ip) && (!onlyHosting || hostingOf(l.isp) !== null))
                           .findIndex(l => placeKeyOf(l) === placePick);
    if (first < 0) return;
    if (first >= LOG_PAGE * more) { setMore(Math.ceil((first + 1) / LOG_PAGE)); return; }
    scrolledTick.current = pickTick;
    const raf = window.requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>('[data-place-first="1"]');
      if (!el) return;
      const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
      if (!reduce) el.animate?.([ { boxShadow: 'inset 0 0 0 2px #5D97F1' }, { boxShadow: 'inset 0 0 0 0 rgba(93,151,241,0)' } ],
                                { duration: 1800, delay: 350, easing: 'ease-out' });
    });
    return () => window.cancelAnimationFrame(raf);
  }, [placePick, pickTick, more, ip, onlyHosting, sliceLogs]);
  const placeRows  = placePick ? sliceLogs.filter(l => placeKeyOf(l) === placePick) : [];
  const placeLabel = placeRows[0] ? (placeOf(placeRows[0]) || 'Lieu inconnu') : '';

  // ── Villes ──
  const cities = useMemo(() => {
    const m = new Map<string, { n: number; suspect: boolean }>();
    sliceLogs.forEach(l => {
      if (!l.city) return;
      const e = m.get(l.city) ?? { n: 0, suspect: false };
      e.n++; if (burst && l.ipAddress === burst.ip) e.suspect = true;
      m.set(l.city, e);
    });
    return Array.from(m.entries()).sort((a, b) => b[1].n - a[1].n).slice(0, 7);
  }, [sliceLogs, burst]);
  const topCity = Math.max(1, ...cities.map(([, e]) => e.n));

  // ── Journal ──
  const journal = sliceLogs.filter(l => (!ip || l.ipAddress === ip) && (!onlyHosting || hostingOf(l.isp) !== null));
  const visible = journal.slice(0, LOG_PAGE * more);
  // « Voir les tentatives » : le journal est plus bas dans la page, et une tranche figée ou le filtre
  // VPN/hébergeurs le videraient. On les lève, on filtre sur l'adresse, puis on amène le journal à l'écran.
  const showAttempts = (addr: string) => {
    setPinned(null); setHovered(null); setOnlyHosting(false); setIp(addr);
    window.requestAnimationFrame(() => journalRef.current?.scrollIntoView({
      behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' }));
  };
  const liveVisits = logs.filter(l => l.anon?.live).length;
  const noun = anonymous ? { one: 'visionnage', many: 'visionnages' } : { one: 'connexion', many: 'connexions' };
  const periodText = period === 'today' ? "aujourd'hui" : period === 'yesterday' ? 'hier' : period === 'week' ? 'sur 7 jours' : period === 'month' ? 'ce mois-ci' : 'sur la période';

  return (
    <div className={styles.root}>
      {user !== 0 &&
        <Box width="95vw" height="95vh" onClose={() => setUser(0)} logo={true}>
          <UserInformations2 userId={user} />
        </Box>}

      {/* ── Période ── */}
      <div className={styles.toolbar}>
        {isSuper && !creatorMode && <Segment label="Vue" value={view} onChange={setView}
                             choices={[ ['accounts', 'Créateur'], ['anonymous', 'Spectateur'] ] as const} />}
        <Segment label="Période" value={period} onChange={setPeriod}
                 choices={[ ['today', "Aujourd'hui"], ['yesterday', 'Hier'], ['week', '7 jours'], ['month', 'Ce mois-ci'], ['custom', 'Personnaliser'] ] as const} />
        {period === 'custom' && <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} />}
        <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
          <input type="text" value={query} onChange={e => setQuery(e.target.value)} aria-label="Rechercher dans tout l'écran"
                 placeholder={anonymous ? (creatorMode ? 'Rechercher : ville, appareil, contenu, heure…' : 'Rechercher : IP, ville, appareil, contenu, heure…') : 'Rechercher : compte, IP, ville, fournisseur, heure…'}
                 style={{ minHeight: 34, minWidth: 280, padding: '6px 28px 6px 10px', border: '1px solid #3B5A86', borderRadius: 6, background: '#152B4A', color: '#E6EDF7', font: 'inherit', fontSize: '0.85rem', colorScheme: 'dark' }} />
          {query && <button type="button" onClick={() => setQuery('')} aria-label="Effacer la recherche"
                            style={{ position: 'absolute', right: 2, width: 26, height: 26, border: 'none', borderRadius: '50%', background: 'transparent', color: '#9FB1CC', cursor: 'pointer' }}>✕</button>}
        </span>
        {needle && <span className={styles.kpiMuted} aria-live="polite">{plural(logs.length, noun.one, noun.many)} sur {rawLogs.length}</span>}
        <span className={styles.spacer}/>
        {error
          ? <span className={tabs.red}>Relevé impossible</span>
          : readAt && <span className={tabs.liveInd}>{loading ? 'Relevé…' : `Relevé à ${hour(readAt.toISOString())}`}</span>}
        <button type="button" className={styles.secondary} onClick={load} title="Actualiser">↻</button>
      </div>

      {/* ── Chiffres ── */}
      <div className={styles.kpis}>
        <button type="button" className={`${styles.kpi} ${styles.kpiBtn} ${tile === null ? styles.kpiOn : ''}`} aria-pressed={tile === null}
                title="Cliquer pour tout montrer" onClick={() => setTile(null)}>
          <span className={styles.kpiLabel}>{anonymous ? 'Visionnages' : 'Connexions'} {periodText}</span>
          <span className={styles.kpiValue}>{logs.length}</span>
          <span className={styles.kpiMuted}>{anonymous ? (liveVisits > 0 ? `${liveVisits} en cours` : (creatorMode ? 'anonymes ou connectés, sans noms' : 'anonymes ou connectés, depuis le portail et les apps TV')) : period === 'today' ? `dont ${lastHour} dans la dernière heure` : `${ok.length} réussies`}</span>
          {viaHosting.length > 0 &&
            <span className={styles.kpiMuted}>{viaHosting.length} par un VPN ou un hébergeur{failsViaHosting > 0 ? <>, <span className={tabs.red}>dont {plural(failsViaHosting, 'échec', 'échecs')}</span></> : ''}</span>}
        </button>
        <KpiTile selectable={!anonymous} on={tile === 'firsts'} title="Cliquer pour ne montrer que les premières connexions (graphique, carte, villes, journal)" onClick={() => setTile(tile === 'firsts' ? null : 'firsts')}>
          <span className={styles.kpiLabel}>{anonymous ? (creatorMode ? 'Visiteurs distincts' : 'Adresses IP distinctes') : 'Comptes distincts'}</span>
          <span className={styles.kpiValue}>{anonymous ? new Set(logs.map(l => l.ipAddress)).size : accounts}</span>
          {anonymous
            ? <span className={styles.kpiMuted}>un foyer peut avoir plusieurs appareils</span>
            : <span className={firsts > 0 ? styles.kpiUp : styles.kpiMuted}>{firsts > 0 ? `${plural(firsts, 'se connecte', 'se connectent')} pour la première fois` : 'aucune première connexion'}</span>}
        </KpiTile>
        {anonymous ? (
          <div className={styles.kpi}>
            <span className={styles.kpiLabel}>Temps de visionnage</span>
            <span className={styles.kpiValue}>{span(logs.reduce((a, l) => a + (l.anon?.seconds ?? 0), 0))}</span>
            <span className={styles.kpiMuted}>cumulé sur la période</span>
          </div>
        ) : (
        <button type="button" className={`${styles.kpi} ${styles.kpiBtn} ${burst ? tabs.kpiAlert : ''} ${tile === 'fails' ? styles.kpiOn : ''}`} aria-pressed={tile === 'fails'}
                title="Cliquer pour ne montrer que les échecs (graphique, carte, villes, journal)" onClick={() => setTile(tile === 'fails' ? null : 'fails')}>
          <span className={styles.kpiLabel}>Échecs</span>
          <span className={`${styles.kpiValue} ${tabs.kpiValueRow} ${fails.length > 0 ? tabs.red : ''}`}>
            {fails.length}{logs.length > 0 && <small>· {failRate.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %</small>}
          </span>
          <span className={styles.kpiMuted}>{worstIpCount >= 2 ? `dont ${worstIpCount} depuis la même adresse IP` : fails.length > 0 ? 'dispersés, sans adresse qui insiste' : 'aucun'}</span>
        </button>
        )}
        <button type="button" className={`${styles.kpi} ${styles.kpiBtn} ${tile !== null && tile !== 'fails' && tile !== 'firsts' ? styles.kpiOn : ''}`} aria-pressed={tile !== null && tile !== 'fails' && tile !== 'firsts'}
                title={ok.length ? `Cliquer pour ne montrer que : ${ORIGIN_LABEL[topOrigin]} (graphique, carte, villes, journal)` : undefined}
                onClick={() => setTile(tile !== null && tile !== 'fails' && tile !== 'firsts' ? null : topOrigin)}>
          <span className={styles.kpiLabel}>{anonymous ? "D'où viennent-ils" : "D'où viennent-elles"}</span>
          <span className={`${styles.kpiValue} ${tabs.kpiValueRow}`}>{ok.length ? <>{Math.round(origins[topOrigin] / originTotal * 100)} %<small>{ORIGIN_LABEL[topOrigin]}</small></> : '–'}</span>
          {ok.length > 0 && <>
            <span className={tabs.split} aria-hidden="true">
              {(Object.keys(origins) as Origin[]).map(o => <span key={o} className={ORIGIN_BAR[o]} style={{ width: `${origins[o] / originTotal * 100}%` }}/>)}
            </span>
            <span className={styles.originLegend}>
              {(Object.keys(origins) as Origin[]).filter(o => origins[o] > 0).map(o => <span key={o}><i className={ORIGIN_BAR[o]}/>{ORIGIN_LABEL[o]} {origins[o]}</span>)}
            </span>
          </>}
        </button>
      </div>

      {/* ── Une adresse qui insiste ── */}
      {burst && (
        <div className={tabs.watch} role="alert">
          <div className={tabs.watchText}>
            <strong>{burst.count} échecs en {plural(burst.minutes, 'minute')} depuis {burst.ip}</strong>
            <span>
              Comptes visés : {Array.from(new Set(burst.items.map(l => l.email))).slice(0, 3).join(', ')}
              {placeOf(burst.items[0]) && ` · ${placeOf(burst.items[0])}`}
              {burst.items[0].isp && ` · ${burst.items[0].isp}`}
              {hostingOf(burst.items[0].isp) && ` (${HOSTING_LABEL[hostingOf(burst.items[0].isp)!.kind].toLowerCase()} : la vraie position est inconnue)`}
            </span>
          </div>
          <button type="button" className={styles.secondary} onClick={() => showAttempts(burst.ip)}>Voir les tentatives</button>
        </div>
      )}

      {/* ── Graphique ── */}
      <div className={tabs.box}>
        <div className={tabs.boxHead}>
          <h3>{chartTitle}</h3>
          <span className={tabs.legend}><span><i style={{ background: '#2C7DE1' }}/>{anonymous ? 'Visionnages' : 'Réussies'}</span>{!anonymous && <span><i style={{ background: '#DC2626' }}/>Échecs</span>}</span>
          <span className={styles.spacer}/>
          {singleDay &&
            <Segment label="Pas du graphique" value={step} onChange={setStep}
                     choices={[ ['minute', 'Minute'], ['tenMinutes', '10 minutes'], ['hour', 'Heure'] ] as const} />}
        </div>
        <Chart buckets={buckets} labelEvery={labelEvery}
               activeFrom={slice?.from ?? null} pinnedFrom={pinned?.from ?? null}
               onHover={i => setHovered(i)} onPick={pick} />
        <div className={tabs.sliceBar} aria-live="polite">
          {pinned
            ? <><span className={tabs.slicePinned}>FIGÉ</span>
                <span>Carte, villes et journal : {noun.many} {sliceText} ({plural(sliceLogs.length, noun.one, noun.many)}).</span>
                <button type="button" className={styles.secondary} onClick={() => setPinned(null)}>Revenir à toute la période</button></>
            : slice
              ? <span>{noun.many.charAt(0).toUpperCase() + noun.many.slice(1)} {sliceText} : {plural(sliceLogs.length, noun.one, noun.many)}. Cliquez la barre pour figer.</span>
              : <span className={styles.muted}>Survolez une barre pour voir ses {noun.many} sur la carte, dans les villes et le journal ; cliquez pour figer.</span>}
        </div>
        {singleDay && step === 'minute' && (
          <div className={tabs.scrubber}>
            <button type="button" className={styles.secondary} aria-label="Une minute plus tôt" disabled={windowEnd <= 60} onClick={() => moveWindow(windowEnd - 1)}>◀</button>
            <div className={tabs.scrubTrack}>
              <input type="range" min={60} max={maxEnd} step={1} value={windowEnd}
                     aria-label="Se déplacer minute par minute dans la journée"
                     aria-valuetext={`de ${clock(windowEnd - 60)} à ${clock(windowEnd)}`}
                     onChange={e => moveWindow(Number(e.target.value))} />
              <div className={tabs.scrubTicks} aria-hidden="true">
                {/* Graduations alignées sur le curseur : sa valeur est la FIN de la fenêtre, de 01:00 à maxEnd. */}
                {[1, 3, 6, 9, 12, 15, 18, 21, 24].filter(h => h * 60 <= maxEnd).map(h =>
                  <span key={h} style={{ left: `${maxEnd > 60 ? (h * 60 - 60) / (maxEnd - 60) * 100 : 0}%` }}>{String(h).padStart(2, '0')} h</span>)}
              </div>
            </div>
            <button type="button" className={styles.secondary} aria-label="Une minute plus tard" disabled={windowEnd >= maxEnd} onClick={() => moveWindow(windowEnd + 1)}>▶</button>
            <span className={tabs.scrubLabel}>{clock(windowEnd - 60)} → {clock(windowEnd)}</span>
            {live && minuteEnd !== null &&
              <button type="button" className={styles.secondary} onClick={() => setMinuteEnd(null)}>Revenir à maintenant</button>}
          </div>
        )}
        {fast && <span className={styles.muted} style={{ fontSize: '0.8rem' }}>La fenêtre suit l'heure qu'il est ; relevé toutes les 20 secondes.</span>}
      </div>

      {/* ── Carte et villes ── */}
      <div className={tabs.twoCol}>
        <div className={tabs.box}>
          <div className={tabs.boxHead}>
            <h3>Carte des connexions</h3>
            {slice && <span className={styles.muted} style={{ fontSize: '0.85rem' }}>{sliceText}</span>}
            <span className={tabs.legend}><span><i style={{ background: COLOR_OK, borderRadius: '50%' }}/>Réussies</span><span><i style={{ background: COLOR_KO, borderRadius: '50%' }}/>Échecs</span></span>
            <span className={styles.spacer}/>
            <Segment label="Connexions montrées" value={mapShow} onChange={setMapShow}
                     choices={[ ['all', 'Toutes'], ['ok', 'Réussies'], ['ko', 'Échecs'] ] as const} />
          </div>
          <div className={tabs.map}>
            <ExtendablePanel logoUrl="/images/logo.png" logoPosition="bottomLeft">
              <OpenStreetMapV2
                coordinates  = {mapPoints.coordinates}
                onPointClick = {mapClick}
                clickToZoom
                initialZoom  = {2}
                renderMode   = "dark"
                mapColors    = {{
                  water     : '#1b2947',
                  land      : '#263d6e',
                  landcover : '#263d6e',
                  road      : '#2a2a4a',
                  building  : '#222244',
                  labelText : '#c8d6e5',
                  labelHalo : '#0d1b2a',
                }}
                cityLabelsOnly
                // Pas de recadrage pendant qu'on parcourt les barres : la vue sauterait à chaque survol.
                autoCenter   = {slice === null}
                fitPoints    = {true}
              />
            </ExtendablePanel>
            <span className={tabs.mapNote}>
              {plural(mapPoints.places, 'lieu', 'lieux')}{mapPoints.unlocated > 0 ? ` · ${mapPoints.unlocated} sans position` : ''}
            </span>
          </div>
        </div>
        <div className={tabs.box}>
          <div className={tabs.boxHead}><h3>Villes</h3><span className={styles.muted} style={{ fontSize: '0.85rem' }}>{slice ? sliceText : "d'après l'adresse IP"}</span></div>
          {cities.length === 0
            ? <div className={styles.muted}>Aucune ville connue {periodText}.</div>
            : <div className={tabs.bars}>
                {cities.map(([city, e]) => (
                  <div key={city} className={tabs.barRow}>
                    <span title={city}>{city}</span>
                    <span className={`${tabs.track} ${e.suspect ? tabs.trackRed : ''}`}><i style={{ width: `${e.n / topCity * 100}%` }}/></span>
                    <span>{e.n}</span>
                  </div>
                ))}
              </div>}
        </div>
      </div>

      {/* ── Journal ── */}
      <div className={tabs.boxHead} ref={journalRef} style={{ scrollMarginTop: 12 }}>
        <h3 className={tabs.sectionTitle}>{anonymous ? 'Journal des visionnages' : 'Journal des connexions'}{slice ? ` ${sliceText}` : ''}</h3>
        {ip
          ? <span className={tabs.ipFilter}>{anonymous ? 'Visionnages' : 'Tentatives'} depuis <span className={tabs.mono}>{ip}</span>
              <button type="button" className={styles.secondary} onClick={() => setIp(null)}>Tout afficher</button></span>
          : <span className={styles.muted} style={{ fontSize: '0.85rem' }}>{anonymous ? (creatorMode ? 'la plus récente en haut' : 'la plus récente en haut ; cliquer une adresse filtre le journal') : 'la plus récente en haut ; cliquer un compte ouvre sa fiche'}</span>}
        {placeRows.length > 0 &&
          <span className={tabs.ipFilter} aria-live="polite">
            <span style={{ width: 9, height: 9, borderRadius: '50%', display: 'inline-block', background: placePick?.endsWith('|1') ? COLOR_OK : COLOR_KO }} />
            Sélection : <strong>{placeLabel}</strong> · {plural(placeRows.length, noun.one, noun.many)}
            <button type="button" className={styles.secondary} onClick={() => setPlacePick(null)}>Effacer</button>
          </span>}
        <span className={styles.spacer}/>
        <button type="button" aria-pressed={onlyHosting} className={onlyHosting ? `${styles.secondary} ${tabs.toggleOn}` : styles.secondary}
                onClick={() => setOnlyHosting(v => !v)}
                title="Connexions passées par un VPN, un hébergeur ou un relais : la ville affichée est celle du serveur">
          {onlyHosting ? '✓ ' : ''}VPN et hébergeurs seulement
        </button>
      </div>

      {journal.length === 0
        ? <div className={styles.emptyState}><div className={styles.emptyText}>
            <strong>{loading ? 'Chargement…' : needle ? `Aucun résultat pour « ${query.trim()} »` : anonymous ? `Aucun visionnage ${periodText}` : `Aucune connexion ${periodText}`}</strong>
            {!loading && <span>Le journal se relit chaque minute.</span>}
          </div></div>
        : <div className={styles.tableBox}>
            <table className={`${styles.table} ${tabs.tableWide}`}>
              <thead>
                {anonymous
                  ? (creatorMode
                      ? <tr><th>Heure</th><th>État</th><th>Contenu regardé</th><th>Appareil</th><th>Origine</th><th>Lieu</th></tr>
                      : <tr><th>Heure</th><th>État</th><th>Contenu regardé</th><th>Compte</th><th>Appareil</th><th>Origine</th><th>Lieu</th><th>Adresse IP</th><th>Fournisseur</th><th>Système · navigateur</th></tr>)
                  : <tr><th>Heure</th><th>Résultat</th><th>Compte</th><th>Type</th><th>Origine</th><th>Lieu</th><th>Adresse IP</th><th>Fournisseur</th></tr>}
              </thead>
              <tbody>
                {visible.map((l, i) => {
                  const fresh = !!l.date && Date.now() - l.date.getTime() < FRESH_MS;
                  const suspect = !!burst && l.ipAddress === burst.ip;
                  const o = originOf(l);
                  const onPlace = placePick !== null && placeKeyOf(l) === placePick;
                  const firstOnPlace = onPlace && !visible.slice(0, i).some(x => placeKeyOf(x) === placePick);
                  return (
                    <tr key={`${l.id}-${l.date?.getTime()}-${i}`} className={`${isOk(l) ? '' : tabs.rowFail} ${fresh ? tabs.rowFresh : ''} ${suspect ? tabs.rowIp : ''}`}
                        aria-selected={onPlace || undefined} data-place-first={firstOnPlace ? '1' : undefined}
                        style={onPlace ? PLACE_ROW : undefined}>
                      <td className={`${styles.nowrap} ${tabs.num}`}>{l.date ? (period === 'today' ? '' : `${dayLabel(l.date.toISOString())} `) + l.date.toLocaleTimeString('fr-FR') : '—'}</td>
                      {l.anon ? <>
                        <td><span className={`${tabs.status} ${tabs.statusOk}`} title={l.anon.endReason ?? undefined}>{l.anon.live ? 'En cours' : span(l.anon.seconds)}</span></td>
                        <td title={l.anon.creatorName ? `Créateur : ${l.anon.creatorName}` : undefined}>{l.anon.target.type === 'video' ? 'Vidéo' : 'Chaîne'} « {l.email} »</td>
                        {!creatorMode && <td>{l.anon.account ?? <span className={styles.muted}>anonyme</span>}</td>}
                        <td>{l.anon.client?.device ?? '—'}</td>
                      </> : <>
                      <td><span className={`${tabs.status} ${isOk(l) ? tabs.statusOk : tabs.statusKo}`} title={l.reason ?? undefined}>{isOk(l) ? 'Réussie' : 'Refusée'}</span></td>
                      <td>
                        {l.unknown
                          ? <><span title="Aucun compte ne porte cet identifiant">{l.email}</span><span className={tabs.first} style={{ background: '#3A1420', color: '#FCA5A5' }}>COMPTE INCONNU</span></>
                          : <button type="button" className={tabs.linkLike} onClick={() => setUser(l.id)}>{l.email}</button>}
                        {l.firstTime && <span className={tabs.first}>1RE FOIS</span>}
                      </td>
                      <td>{l.role === 'créateur' ? 'Créateur' : l.role === 'user' ? 'Spectateur' : '—'}</td>
                      </>}
                      <td><span className={`${tabs.origin} ${ORIGIN_CLASS[o]}`}>{o === 'other' ? (l.origin || '—') : ORIGIN_LABEL[o]}</span>{creatorMode && l.anon?.identified && <span className={styles.muted} title="Visiteur connecté à un compte (nom non communiqué)"> · connecté</span>}</td>
                      <td>{placeOf(l) || '—'}</td>
                      {!creatorMode && <td className={`${tabs.mono} ${suspect ? tabs.red : ''}`}>
                        {l.ipAddress
                          ? <button type="button" className={tabs.linkLike} style={{ fontWeight: 400, color: 'inherit' }} title="Ne montrer que cette adresse" onClick={() => showAttempts(l.ipAddress)}>{l.ipAddress}</button>
                          : '—'}
                      </td>}
                      {!creatorMode && <td><Provider isp={l.isp} /></td>}
                      {!creatorMode && l.anon && <td title={l.anon.userAgent ?? (l.anon.client ? `Vu le ${new Date(l.anon.client.seenAt).toLocaleString('fr-FR')} (${l.anon.client.source})` : undefined)}>
                        {[l.anon.client?.os, l.anon.client?.browser ?? l.anon.client?.app].filter(Boolean).join(' · ') || '—'}
                      </td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>}
      {journal.length > visible.length &&
        <button type="button" className={styles.secondary} onClick={() => setMore(m => m + 1)}>
          Afficher {Math.min(LOG_PAGE, journal.length - visible.length)} de plus ({journal.length - visible.length} restantes)
        </button>}
    </div>
  );
};

export default TrafficSection;
